import React from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Unstable_Grid2';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, FileText, AlertCircle } from 'lucide-react';

import { getUser, getClientPermissions } from '../service/weexpertService';
import mainImg from '../assets/img/Cover.png';
import MainPageRendered from './pages/main.page.rendered';

const MainPage = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const handleGoToDocuments = () => {
    navigate('/account?tab=documents');
  };

  // Get current user data
  const { data: userData, isLoading: userLoading, error: userError } = useQuery({
    queryKey: ['userMe'],
    queryFn: getUser,
    retry: false,
    refetchOnWindowFocus: false
  });

  // Get client permissions (only for CLIENT users)
  const isClient = userData?.data?.roles === 'CLIENT';
  const {
    data: permissionsData,
    isLoading: permissionsLoading,
    error: permissionsError
  } = useQuery({
    queryKey: ['clientPermissions'],
    queryFn: getClientPermissions,
    enabled: isClient, // Only run this query for CLIENT users
    retry: false,
    refetchOnWindowFocus: false
  });

  const bgStyleForMain = {
    padding: 0,
    height: '94.4vh',
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    backgroundImage: `url(${mainImg})`,
    backgroundSize: 'cover',
    backgroundPosition: ' 0 center',
    backgroundRepeat: 'no-repeat'
  };

  // Loading state
  if (userLoading || (isClient && permissionsLoading)) {
    return (
      <Grid container sx={bgStyleForMain}>
        <div className="flex items-center justify-center w-full h-full">
          <Card className="w-96 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                <span>{t('loading')}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </Grid>
    );
  }

  // Error state
  if (userError || (isClient && permissionsError)) {
    return (
      <Grid container sx={bgStyleForMain}>
        <div className="flex items-center justify-center w-full h-full">
          <Card className="w-96 shadow-lg">
            <CardContent className="p-6">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {t('error_loading_data')}
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>
      </Grid>
    );
  }

  // For non-CLIENT users, show the original main page
  if (!isClient) {
    return <MainPageRendered/>
    return <Grid container sx={bgStyleForMain} />;
  }

  // For CLIENT users, check if they have platform access
  const canAccessPlatform = permissionsData?.data?.canClientAccessPlatform || ( Array.isArray(userData.data.features) && userData.data.features.includes("FEE_PAYMENT") === false);

  // If client has access, show the original main page
  if (canAccessPlatform) {
    return <MainPageRendered/>
    return <Grid container sx={bgStyleForMain} />;
  }
  // If client doesn't have access, show the waiting message
  return (
    <Grid container sx={bgStyleForMain}>
      <div className="flex items-center justify-center w-full h-full p-4">
        <Card className="max-w-2xl shadow-lg">
          <CardContent className="p-8">
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="bg-yellow-100 rounded-full p-4">
                  <Clock className="h-8 w-8 text-yellow-600" />
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  {t('verification_in_progress')}
                </h2>
                <p className="text-gray-600 leading-relaxed">
                  {t('verification_wait_message')}
                </p>
              </div>

              <Alert>
                <FileText className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-3">
                    <div>
                      <strong>{t('important')}:</strong> {t('upload_documents_reminder')}
                    </div>
                    <Button
                      onClick={handleGoToDocuments}
                      className="w-full sm:w-auto"
                      size="sm"
                    >
                      {t('upload_documents_button')}
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>

              <div className="text-sm text-gray-500">
                <p>
                  {t('verification_time_notice')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Grid>
  );
};

export default MainPage;
